import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { useAuth } from './cognito';

/**
 * Returns an axios response with Authorization provided by cognito
 *
 * @param {Object} config is the default config to be sent to the axios
 *
 * @returns {Object} response, error, loading, sendData
 * @returns {Object} response is the response from the axios request
 * @returns {Object} error is the error from the axios request
 * @returns {Boolean} loading is the loading state of the axios request
 * @returns {Function} sendData is the function to send the axios request
 *
 */
export const useAxios = (axiosParams: AxiosRequestConfig) => {
  const { token, initialized: authInitialized, tokenReady } = useAuth();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [loading, setLoading] = useState(
    axiosParams.method === 'GET' || axiosParams.method === 'get',
  );

  const configWithAuth: AxiosRequestConfig = {
    ...axiosParams,
    headers: {
      ...(axiosParams.headers || {}),
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      Authorization: authInitialized && tokenReady && token ? `Bearer ${token}` : '',
    },
  };

  // Debug logging
  console.log('Auth state:', { authInitialized, tokenReady, hasToken: !!token, tokenLength: token?.length });

  const fetchData = async (params: AxiosRequestConfig) => {
    try {
      const result = await axios.request(params);
      setResponse(result);
    } catch (err /*: AxiosError */) {
      setError(err as AxiosError);
    } finally {
      setLoading(false);
    }
  };

  const sendData = (params?: any, data?: any) => {
    setLoading(true);
    fetchData({
      ...configWithAuth,
      params,
      data,
    });
  };

  // This is a generic request function that can be used to send any request
  const request = async (options: any): Promise<AxiosResponse> => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      axios
        .request({
          ...configWithAuth,
          ...options,
        })
        .then(response => {
          resolve(response);
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  useEffect(() => {
    if (authInitialized && tokenReady) {
      if (axiosParams.method === 'GET' || axiosParams.method === 'get') {
        fetchData(configWithAuth);
      }
    }
  }, [authInitialized, tokenReady]);

  return { response, error, loading, sendData, request };
};

export default { useAxios };
